'use client';

import Image from 'next/image';
import Link from 'next/link';
import { useState } from 'react';

export interface RouteRoyalService {
  id: string;
  number?: string;
  title: string;
  description: string;
  link?: string;
  linkText?: string;
  icon?: string;
  image?: string;
}

export interface RouteRoyalServicesSectionProps {
  title?: string;
  subtitle?: string;
  services?: RouteRoyalService[];
  featuredImage?: string;
  messages?: Record<string, unknown>;
  locale?: 'en' | 'es' | 'fr';
}

const safeGetString = (obj: unknown, defaultValue: string): string => {
  if (typeof obj === 'string') return obj;
  return defaultValue;
};

export function RouteRoyalServicesSection({
  title,
  subtitle,
  services,
  featuredImage,
  messages,
}: RouteRoyalServicesSectionProps) {
  const servicesMsg = messages?.services as Record<string, unknown> | undefined;
  const defaultServicesMsg = servicesMsg?.defaultServices as Record<string, Record<string, unknown>> | undefined;

  const displayTitle = title || safeGetString(servicesMsg?.whatWeOffer, 'Our Services');
  const displaySubtitle = subtitle || safeGetString(servicesMsg?.whatWeOfferSubtitle, 'Comprehensive real estate solutions tailored to your needs.');

  const defaultServices: RouteRoyalService[] = services || [
    {
      id: '1', number: '01',
      title: safeGetString(defaultServicesMsg?.homeBuying?.title, 'Property Advisory'),
      description: safeGetString(defaultServicesMsg?.homeBuying?.description, 'Expert guidance through every step of your property acquisition journey.'),
    },
    {
      id: '2', number: '02',
      title: safeGetString(defaultServicesMsg?.valuation?.title, 'Market Valuation'),
      description: safeGetString(defaultServicesMsg?.valuation?.description, 'Data-driven property assessments backed by comprehensive market analysis.'),
    },
    {
      id: '3', number: '03',
      title: safeGetString(defaultServicesMsg?.investment?.title, 'Investment Strategy'),
      description: safeGetString(defaultServicesMsg?.investment?.description, 'Unlock real estate investment potential with expert portfolio analysis and planning.'),
    },
    {
      id: '4', number: '04',
      title: safeGetString(defaultServicesMsg?.closings?.title, 'Transaction Management'),
      description: safeGetString(defaultServicesMsg?.closings?.description, 'End-to-end coordination ensuring smooth, stress-free property transactions.'),
    },
  ];

  const [expandedId, setExpandedId] = useState<string | null>(defaultServices[1]?.id || null);

  return (
    <section className="py-20 lg:py-28 bg-white">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-14">
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-gray-900 mb-4 tracking-tight">
            {displayTitle}
          </h2>
          {displaySubtitle && (
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">{displaySubtitle}</p>
          )}
        </div>

        <div className="relative max-w-4xl mx-auto">
          {featuredImage && (
            <div className="hidden lg:block absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-72 h-72 z-10 pointer-events-none">
              <div className="relative w-full h-full rounded-xl overflow-hidden shadow-xl">
                <Image src={featuredImage} alt="" fill className="object-cover" sizes="288px" />
              </div>
            </div>
          )}

          <div className="divide-y divide-gray-200 border-y border-gray-200">
            {defaultServices.map((service) => {
              const expanded = expandedId === service.id;
              return (
                <div key={service.id}>
                  <button
                    onClick={() => setExpandedId(expanded ? null : service.id)}
                    className="w-full text-left py-6 lg:py-8 flex items-center justify-between gap-6 group"
                  >
                    <h3 className={`text-xl lg:text-2xl font-medium tracking-tight transition-colors duration-150 ${
                      expanded ? 'text-blue-900 font-semibold' : 'text-gray-400 group-hover:text-gray-600'
                    }`}>
                      {service.title}
                    </h3>
                    {service.number && (
                      <span className={`text-xl lg:text-2xl font-light tabular-nums transition-colors duration-150 ${
                        expanded ? 'text-blue-900' : 'text-gray-400'
                      }`}>
                        {service.number}
                      </span>
                    )}
                  </button>
                  <div className={`overflow-hidden transition-all duration-400 ${expanded ? 'max-h-60 opacity-100' : 'max-h-0 opacity-0'}`}>
                    <div className="pb-6 lg:pb-8 pr-4 md:pr-16">
                      <p className="text-gray-600 leading-relaxed mb-4">{service.description}</p>
                      {service.link && (
                        <Link href={service.link} className="inline-flex items-center gap-1.5 text-sm font-medium text-blue-700 hover:text-blue-800 underline underline-offset-4 decoration-1">
                          {service.linkText || 'Learn more'}
                        </Link>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </section>
  );
}
